/**
 * `DIALOG.parentPrompt` (W2-07; DESIGN.md §4.21 "Parent prompt", §5.5 "Parent", F§10.4). After choosing a parent
 * (or removing it): "Keep its numbers" (default; RPE behaviour: the line moves with the parent) or "Keep it where it
 * is now" (its position is converted into the parent's frame at the playhead; for an animated position only the
 * keyframe at or after the playhead is converted, which the dialog says). With a parent, the "Also turn with parent"
 * switch has a two-frame diagram and the sentence of F§10.4.
 *
 * The caller performs the parent change with the chosen mode. Opened by the Lines parent flow (a matching
 * `ParentRequest` in the Lines UI store), the prompt covers every line of the request (title, animated-position
 * note, a mixed switch when they differ) and hands a changed switch back through the request, so the flow applies it
 * to all of them in the parent change's own transaction. Opened by another package (inspector, preview), it can only
 * know the one line of its props: a changed switch is applied here with the coalescing key `parentCoalesceKey`, so a
 * caller that uses the same key gets one undo step for both (change request CR-2).
 */

import { useState } from 'react';
import type { DialogProps } from '../../app/registry/types';
import { Core } from '../../core/commands';
import type { LineId } from '../../core/model/types';
import { useT } from '../../i18n/useT';
import type { DocStore } from '../../state/types';
import { Button } from '../../ui/Button';
import { Checkbox } from '../../ui/Checkbox';
import { Dialog } from '../../ui/Dialog';
import { Icon } from '../../ui/Icon';
import { useServicesStore } from './hooks';
import { lineTitle, parentCoalesceKey } from './lineActions';
import { m } from './lines.messages';
import { linesUi, type ParentRequest } from './linesUi';
import s from './Lines.module.css';

type Mode = 'keepNumbers' | 'keepPlace';

/** True when the line's position (any layer of X or Y) has more than one keyframe. */
export function positionAnimated(store: Pick<DocStore, 'idx'>, line: LineId): boolean {
  const tracks = store.idx.tracksOfLine(line);
  for (const prop of ['moveX', 'moveY'] as const) {
    for (const tr of tracks.get(prop) ?? []) if (store.idx.keysByTrack(tr).length > 1) return true;
  }
  return false;
}

/** The switch over several lines: on or off for all, or mixed. */
export type TurnState = boolean | 'mixed';

export function turnStateOf(store: Pick<DocStore, 'doc'>, lines: readonly LineId[]): TurnState {
  let on = 0;
  let off = 0;
  for (const id of lines) {
    const line = store.doc.lines.get(id);
    if (line === undefined) continue;
    if (line.rotateWithParent) on++;
    else off++;
  }
  return on > 0 && off > 0 ? 'mixed' : on > 0;
}

/** The Lines parent flow's request when it opened this prompt (same line and parent), else null. */
function matchingRequest(p: DialogProps<'parentPrompt'>): ParentRequest | null {
  const r = linesUi(p.services).parentRequest;
  return r !== null && r.line === p.props.line && r.parent === p.props.parent ? r : null;
}

const DEG = Math.PI / 180;

/** One frame of the "Also turn with parent" diagram: the parent turned by 30 degrees, the child on its circle. */
function TurnFrame(props: { readonly turns: boolean; readonly caption: string }) {
  const cx = 60;
  const cy = 64;
  const theta = -30 * DEG;
  const [ox, oy] = [44, -26];
  const px = cx + Math.cos(theta) * ox - Math.sin(theta) * oy;
  const py = cy + Math.sin(theta) * ox + Math.cos(theta) * oy;
  const r = Math.hypot(ox, oy);
  const childAngle = props.turns ? theta : 0;
  const half = 16;
  const dx = Math.cos(childAngle) * half;
  const dy = Math.sin(childAngle) * half;
  const pdx = Math.cos(theta) * 34;
  const pdy = Math.sin(theta) * 34;
  return (
    <figure className={s.diagramFrame}>
      <svg className={s.diagramSvg} viewBox="0 0 140 110" aria-hidden="true">
        <circle className={s.svgGuide} cx={cx} cy={cy} r={r} />
        <line className={s.svgParent} x1={cx - pdx} y1={cy - pdy} x2={cx + pdx} y2={cy + pdy} />
        <circle className={s.svgDot} cx={cx} cy={cy} r={3} />
        <line className={s.svgChild} x1={px - dx} y1={py - dy} x2={px + dx} y2={py + dy} />
        <circle className={s.svgDot} cx={px} cy={py} r={2.5} />
      </svg>
      <figcaption>{props.caption}</figcaption>
    </figure>
  );
}

export function ParentPromptDialog(p: DialogProps<'parentPrompt'>) {
  const t = useT();
  const store = useServicesStore(p.services);
  const [request] = useState<ParentRequest | null>(() => matchingRequest(p));
  const lines = (request?.lines ?? [p.props.line]).filter((id) => store?.doc.lines.has(id) === true);
  const initialTurn = store === null ? false : turnStateOf(store, lines);
  const [mode, setMode] = useState<Mode>('keepNumbers');
  const [turns, setTurns] = useState<TurnState>(initialTurn);
  const line = store?.doc.lines.get(p.props.line);
  if (store === null || line === undefined) return null;
  const parent = p.props.parent;
  const name = lineTitle(store, line.id);
  const many = lines.length > 1;
  const animatedLine = lines.find((id) => positionAnimated(store, id));

  const close = (): void => {
    p.close(null);
  };
  const confirm = (): void => {
    if (parent !== null && turns !== 'mixed') {
      if (request !== null) {
        // The Lines parent flow applies it with the parent change (one transaction).
        request.answerTurn(lines.some((id) => store.doc.lines.get(id)?.rotateWithParent !== turns) ? turns : null);
      } else if (turns !== line.rotateWithParent) {
        const out = store.transact(
          {
            label: { key: turns ? m.txTurnOn : m.txTurnOff, params: { line: name } },
            command: 'lines.setParent',
            coalesceKey: parentCoalesceKey(line.id),
          },
          (tx) => {
            Core.lines.patchLine(tx, { id: line.id, patch: { rotateWithParent: turns } });
          },
        );
        if (!out.ok) {
          p.services.ui.toast(out.reason, { kind: 'error' });
          return;
        }
      }
    }
    p.close(mode);
  };
  const heading = (): string => {
    if (parent === null) {
      return many ? t(m.parentTitleDetachMany, { count: lines.length }) : t(m.parentTitleDetach, { line: name });
    }
    const parentName = lineTitle(store, parent);
    return many
      ? t(m.parentTitleMany, { count: lines.length, parent: parentName })
      : t(m.parentTitle, { line: name, parent: parentName });
  };

  const choice = (value: Mode, title: string, text: string) => (
    <label className={s.choice}>
      <input
        type="radio"
        name="parentMode"
        value={value}
        checked={mode === value}
        onChange={() => {
          setMode(value);
        }}
      />
      <span className={s.choiceTitle}>{title}</span>
      <span className={s.choiceText}>{text}</span>
    </label>
  );

  return (
    <Dialog
      open={true}
      size="md"
      title={heading()}
      onClose={close}
      footer={
        <>
          <Button variant="secondary" onClick={close}>
            {t(m.cancel)}
          </Button>
          <Button variant="primary" icon={parent === null ? 'Unlink' : 'Network'} onClick={confirm}>
            {t(parent === null ? m.parentConfirmDetach : m.parentConfirm)}
          </Button>
        </>
      }
    >
      <div className={s.form}>
        <fieldset className={s.choices}>
          <legend className={s.label}>{t(many ? m.parentHowMany : m.parentHow)}</legend>
          {choice('keepNumbers', t(m.keepNumbers), t(m.keepNumbersHelp))}
          {choice(
            'keepPlace',
            t(m.keepPlace),
            animatedLine === undefined
              ? t(m.keepPlaceHelp)
              : t(m.keepPlaceAnimated, { line: lineTitle(store, animatedLine) }),
          )}
        </fieldset>
        {parent !== null && (
          <>
            <Checkbox
              label={t(m.turnWithParent)}
              checked={turns}
              onChange={(v) => {
                setTurns(v);
              }}
            />
            {turns === 'mixed' && <p className={s.help}>{t(m.turnMixed)}</p>}
            <div className={s.diagram}>
              <TurnFrame turns={false} caption={t(m.turnOffCaption)} />
              <TurnFrame turns={true} caption={t(m.turnOnCaption)} />
            </div>
            <p className={s.sentence}>{t(m.turnSentence)}</p>
          </>
        )}
        <div className={s.info}>
          <Icon name="Info" size={16} />
          <span>{t(m.notInherited)}</span>
        </div>
      </div>
    </Dialog>
  );
}
